import type { PublicShare, PublicShareItem } from './types';

const markdownContentCache = new Map<string, string>();

function resolvePublicShareEndpoint(params: {
  agentId?: string;
  shareId?: string;
  publicId?: string;
}): string {
  const agentId = params.agentId?.trim();
  const shareId = params.shareId?.trim();
  if (agentId && shareId) {
    return `/v1/public/agent-shares/${encodeURIComponent(agentId)}/${encodeURIComponent(shareId)}`;
  }

  const publicId = params.publicId?.trim();
  if (publicId) {
    return `/v1/public/shares/${encodeURIComponent(publicId)}`;
  }

  throw new Error('Invalid share URL');
}

export async function fetchPublicShare(params: {
  agentId?: string;
  shareId?: string;
  publicId?: string;
}): Promise<PublicShare> {
  const endpoint = resolvePublicShareEndpoint(params);
  const response = await fetch(endpoint, {
    headers: {
      accept: 'application/json',
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { message?: string };
    } | null;
    throw new Error(
      body?.error?.message ?? `Failed to load share (${response.status})`
    );
  }

  return (await response.json()) as PublicShare;
}

export async function fetchMarkdownContent(
  url: string,
  signal?: AbortSignal
): Promise<string> {
  const cached = markdownContentCache.get(url);
  if (cached !== undefined) {
    return cached;
  }

  const response = await fetch(url, {
    signal,
    headers: {
      accept: 'text/markdown,text/plain;q=0.9,*/*;q=0.8',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load markdown (${response.status})`);
  }

  const text = await response.text();
  markdownContentCache.set(url, text);
  return text;
}

export function getCachedMarkdownContent(url: string): string | undefined {
  return markdownContentCache.get(url);
}

export function resolveDefaultSelectedItem(
  items: PublicShareItem[]
): PublicShareItem | null {
  if (items.length === 0) {
    return null;
  }
  return items.find(item => item.kind === 'markdown') ?? items[0];
}

export function findPreviewImage(items: PublicShareItem[]): string | null {
  return items.find(item => item.kind === 'image')?.content_url ?? null;
}
